<script lang="ts">
    import { computed, toRaw } from 'vue';
    import vm, { JourneyNode, JourneyTextNode } from '@journeys/Journeys.viewmodel';
    import { NodeType } from '@journeys/Enums';

    // components
    import ContainerFloating from '../../../shared/components/ContainerFloating.vue';
    import FluidButton from '@cwd/shared/components/FluidButton.vue';
    import LineMenu from '../Canvas/LineMenu.vue';

    // import { ENode } from '@cwd/Journeys/binaryWorker';
    import { JourneyLine } from '../../Journeys.viewmodel';
    import { requestFrame, calc_snap } from '../../../shared/utils';

    // assets
    import {
        LineToolSVG,
        NavigationSVG,
        PointerSVG,
        CopySVG,
        // PasteSVG,
        TextSVG,
        TrashLegacySVG,
        // AlignCenterSVG,
        // DistributeSpaceSVG
    } from '@shared/assets/vector/ui/IconsUI';

    // util
    import { deepClone, bitshift, uid, longInput } from '@utils';

    // directives
    import { vTooltip } from '@shared/directives';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'UI_Toolbar',
    };
    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { editor, activeJourney, viewport, settings, toast } = vm;

    const selectTool = (tool: string, ev?: PointerEvent) => {
        editor.events.emit('closecontextmenu');

        if (tool === 'line') {
            let onFail = longInput(() => {
                let evt = ev?.target as HTMLElement,
                    rect = evt.getBoundingClientRect();

                LineMenu._props = {
                    data: editor.state.activeLineStyle,
                    colors: [
                        { clr: 126, tooltip: '' },
                        { clr: 6, tooltip: '' },
                        { clr: 35, tooltip: '' },
                        { clr: 159, tooltip: '' },
                    ],
                };

                editor.events.emit('opencontextmenu', LineMenu, { x: rect.right + settings.ui.rem * 1, y: rect.y }, viewport.dims);
            }, 440);

            document.addEventListener('pointerup', onFail, { once: true });
        }

        editor.state.activeTool = tool;
    };

    const handleCopyToClipboard = () => {
        editor.tools.clipboard = editor.tools.sel_nodes;
        console.log(vm.activeJourney.nodes);
        console.log(vm.activeJourney);

        handlePasteToCanvas();
    };

    // TODO: move this to vm
    const handlePasteToCanvas = () => {
        let copy = deepClone<typeof editor.tools.sel_nodes>(toRaw(editor.tools.clipboard)),
            copyOffset = bitshift.round(Math.random() * 200) + 100,
            newNodes = Object.values(copy).reduce((acc: any, item: any) => {
                // dont let the starting segment to be copied
                if (item.data.type === NodeType.Segment) {
                    // display information that a node has not been copied
                    toast.emit('note', { pos: 'top-center', type: 'success', message: { head: $Resources.StartingNodeCantBeClonedHeading, break: true } });

                    // TODO: right we dont copy lines and connections yet
                    // for (let lineId of Object.values({ ...item.lnto, ...item.lnfr })

                    return acc;
                }

                item.id = editor.tools.seq_iid();
                item.runtime &&= {};
                item.lnto &&= {};
                item.lnfr &&= {};
                item.to &&= '';
                item.fr &&= '';
                item.x += copyOffset;
                item.y += copyOffset;
                item.runtime.cache ||= {};

                delete item.data.isInitial;
                delete item.data.SegmentId;
                delete item.data.IssueId;
                delete item.data.TaskId;
                delete item.data.Schedule;
                delete item.data.startingSegment;

                acc[item.id] = item;

                return acc;
            }, {});

        editor.events.emit('addnodes', newNodes);
    };

    editor.events.addListener('pasteselection', handlePasteToCanvas);

    const selectionArray = computed(() => {
        let items = {
            nodes: Object.values<JourneyNode | JourneyTextNode>(editor.tools.sel_nodes),
            points: Object.values<any>(editor.tools.sel_points),
            lines: Object.values<JourneyLine>(editor.tools.sel_lines),
        };

        editor.tools.sel_count = items.nodes.length + items.lines.length + items.points.length;
        return items;
    });
    const handleDeleteSelection = () => {
        selectionArray.value.nodes.forEach((n: any) => editor.events.emit('deletenode', n.id));

        selectionArray.value.lines.forEach((p: any) => {
            if (!activeJourney.lines[p.id]) return;

            editor.events.emit('deleteline', activeJourney.lines[p.id]);
        });

        editor.tools.sel_keyObj = null;
        editor.tools.sel_nodes = {};
        editor.tools.sel_points = {};
        editor.tools.sel_lines = {};
        editor.tools.highlight = {};
    };

    editor.events.addListener('deleteselection', handleDeleteSelection);
</script>

<template>
    <pre hidden>{{ selectionArray.nodes.length }}</pre>
    <ContainerFloating class="vertical toolbar">
        <FluidButton
            :icon="PointerSVG"
            class="btn btn-default"
            :class="editor.state.activeTool === 'pointer' ? 'active' : ''"
            @click="() => selectTool('pointer')"
            v-tooltip.right="$Resources.JourneysToolbarSelectBtn" />

        <FluidButton
            :icon="NavigationSVG"
            class="btn btn-default"
            :class="editor.state.activeTool === 'nav' ? 'active' : ''"
            @click="() => selectTool('nav')"
            v-tooltip.right="$Resources.JourneysToolbarDragBtn" />

        <FluidButton
            :icon="LineToolSVG"
            class="btn btn-default"
            :class="editor.state.activeTool === 'line' ? 'active' : ''"
            @pointerdown="(ev: PointerEvent) => selectTool('line', ev)"
            v-tooltip.right="$Resources.JourneysToolbarLineToolBtn" />

        <FluidButton
            :icon="TextSVG"
            class="btn btn-default last"
            :class="editor.state.activeTool === 'text' ? 'active' : ''"
            @click="() => selectTool('text')"
            v-tooltip.right="$Resources.JourneysToolbarTextToolBtn" />

        <div class="separator horizontal"></div>

        <FluidButton
            :icon="CopySVG"
            @click="handleCopyToClipboard"
            class="btn btn-default"
            :class="
                (editor.tools.sel_count === 0 || (editor.tools.sel_count === 1 && Object.values(editor.tools.sel_nodes)[0].data.type === NodeType.Segment)) &&
                'inactive'
            "
            v-tooltip.right="$Resources.JourneysToolbarCloneBtn" />

        <!-- <FluidButton :icon="PasteSVG" @click="handlePasteToCanvas"
         :class="Object.keys(editor.tools.clipboard).length === 0 ? 'inactive' : ''" /> -->

        <FluidButton
            :icon="TrashLegacySVG"
            @click="handleDeleteSelection"
            class="btn btn-default"
            :class="editor.tools.sel_count === 0 ? 'inactive' : ''"
            v-tooltip.right="$Resources.JourneysToolbarDeleteBtn" />
    </ContainerFloating>
</template>

<style lang="less">
    .container-floating.toolbar {
        top: 6rem;
        left: 0;
    }

    .container-floating .fluid-button {
        border-radius: 0;
        box-shadow: none !important;
    }

    .container-floating.vertical .fluid-button + .fluid-button {
        margin-top: -1px;
    }
</style>
